import { ChangeDetectorRef, Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SafeUrl } from '@angular/platform-browser';
import { Store } from '@ngrx/store';

import { isObservable, zip as observableZip } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import * as fromApp from '../../app.reducer';
import './vendor/turn';
import { ShowStoryService } from './show.service';
import { CurrentUser } from 'app/services/currentUser.service';
import { AccountService } from 'app/+account/account.service';
import { ProjectService } from 'app/services/project.service';
import { FirebaseService } from 'app/services/firebase.service';
import { DetectPlatformService } from 'app/services/detectPlatform.service';
import { UserAccount } from 'app/services/userAccount.service';
import { LoggingService, LogAppSection } from 'app/services/logging.service';
import { Project } from '../interfaces/project';
import { Story } from '../interfaces/story';
import { Spread } from '../interfaces/spread';
import { ShareLink } from '../interfaces/shareLink';
import { ContribService } from 'app/+projects/contrib/contrib.service';
import { ShareService } from 'app/services/share.service';
import { HeaderComponent } from 'app/header/header.component';
import { FacebookPixelService } from 'app/services/facebook-pixel.service';

@Component({
  selector: 'show-story',
  styleUrls: ['./show.component.scss'],
  templateUrl: './show.component.html',
  providers: [HeaderComponent],
})
export class ShowStoryComponent implements OnInit {
  @Input() pstoryId;
  @Input() isGuest;
  initialSpreads: Spread[];
  spreads: Spread[];
  prioritySpreadsCount = 5;
  story: Story;
  storyCover: SafeUrl;
  currentPage = 0;
  loadedImages = [];
  loadedPlaceholderImages = [];
  isTurnInitialized = false;
  showPageNumbers = false;
  pageNumberTimerId: NodeJS.Timer;
  countPages = 0;
  errorCode = '';
  queryParams = {};
  projectId;
  isShareModalOpened = false;
  isShareLinkGenerationInProgress = false;
  isShareLinkGenerationHasFailed = false;
  shareLink;
  isSuccessfullyCopiedMessageShown = false;
  storyId;
  isAnnotatorEnabled = false;
  invitationToken;
  isReady = false;
  isStoryOwner = false;
  isMobile: boolean;
  submitting: boolean;
  trackedPercentages = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private store: Store<fromApp.AppState>,
    private currentUser: CurrentUser,
    private accountService: AccountService,
    private userAccount: UserAccount,
    private storyService: ShowStoryService,
    private projectService: ProjectService,
    private firebaseService: FirebaseService,
    private loggingService: LoggingService,
    public detectPlatformService: DetectPlatformService,
    private contribService: ContribService,
    private header: HeaderComponent,
    private shareService: ShareService,
    private facebookPixelService: FacebookPixelService,
  ) {

    this.projectId = this.route.snapshot.paramMap.get('uuid');
    this.invitationToken = this.route.snapshot.queryParamMap.get('token');
  }

  ngOnInit() {
    if (this.isGuest) {
      if (!this.header.isLoggedIn()) {
        this.currentUser.createUser().subscribe(d => {
          this.currentUser.set(d);
          this.shareService.acceptShare(this.invitationToken).subscribe(res => { this.getStoryInfo(); });
        });
      } else {
        this.shareService.acceptShare(this.invitationToken).subscribe(res => { this.getStoryInfo(); });
      }
    } else {
      this.getStoryInfo();
    }
  }

  getStoryInfo() {
    observableZip(this.route.queryParams, this.route.params)
      .pipe(
        concatMap(params => {
          this.queryParams = { ...params[0], ...params[1] };
          if (!(this.queryParams ? this.queryParams['id'] : true)) {
            return this.shareService.getProjectInfo(this.route.snapshot.paramMap.get('uuid')).pipe(map(project => {
              this.queryParams['id'] = project.stories[0].id;
              return this.storyService.load(this.queryParams);
            }));
          }
          return this.storyService.load(this.queryParams);
        })
      )
      .subscribe(
        (storyData: any) => {
          if (isObservable(storyData)) {
            storyData.subscribe(resp => {
              storyData = resp;
              this.setStoryData(resp);
              if (this.isGuest) {
                this.firebaseService.logEvent('shared_story_open', {
                  story_id: storyData['id'],
                  project_id: storyData['project'],
                });
              }
            });
          } else {
            this.setStoryData(storyData);
          }
        },
        err => {
          if (this.queryParams['share_token']) {
            this.errorCode = 'share_token_broken';
          } else if (err.status) {
            this.errorCode = err.status;
          } else {
            this.errorCode = 'unknown';
          }
        }
      );

    this.store.select('shared').subscribe(store => {
      this.isMobile = store.isMobile;
    });
    this.store.select('new').subscribe(store => {
      this.submitting = store.submitting;
    });
  }

  setStoryData(storyData) {
    this.initialSpreads = storyData['spreads'];
    this.story = storyData;
    this.spreads = this.initialSpreads.map((item, index, array) => {
      return index >= this.prioritySpreadsCount &&
        index < array.length - this.prioritySpreadsCount
        ? {
          ...item,
          urls: {
            thumbnail: '',
            web: '',
          },
          priority: false,
        }
        : { ...item, priority: true };
    });
    this.projectId = storyData.project;
    this.loggingService.log(
      `Current story. id:${storyData.id}, state:${storyData.state}, project:${storyData.project}, title:${storyData.title}, ` +
      `event_date:${storyData.event_date}, featured:${storyData.featured}, design_mode:${storyData.design_mode}, page_count:` +
      `${storyData.page_count}, hires_uploaded:${storyData.hires_uploaded}, design_progress:${storyData.design_progress}`,
      { appSection: LogAppSection.StoryShowing }
    );
    this.loggingService.log('Spreads:', {
      appSection: LogAppSection.StoryShowing,
    });
    this.loggingService.table(this.spreads, {
      appSection: LogAppSection.StoryShowing,
    });

    if (!this.story.hires_uploaded && !this.queryParams['share_token']) {
      this.watchHires();
    }

    if (this.isLoggedIn()) {
      this.checkIfStoryOwner();
    } else {
      this.isReady = true;
      this.loggingService.log('isReady: ' + this.isReady, {
        appSection: LogAppSection.StoryShowing,
      });
    }

    this.firebaseService.logEvent(
      `${
      this.route.snapshot.queryParamMap.get('share_token')
        ? 'shared_'
        : ''
      }story_open`,
      {
        story_id: this.story.id,
        project_id: this.projectId
      }
    );
    this.facebookPixelService.trackEvent('ViewContent', {
      story_id: this.story.id,
    });
  }

  watchHires() {
    setTimeout(() => {
      this.storyService
        .load({ id: this.story.id })
        .subscribe((storyData: Story) => {
          const { hires_uploaded } = storyData;

          this.loggingService.log('Hires uploaded: ' + hires_uploaded, {
            appSection: LogAppSection.StoryShowing,
          });

          if (hires_uploaded) {
            this.story.hires_uploaded = hires_uploaded;
          } else {
            this.watchHires();
          }
        });
    }, 10000);
  }

  get showLoadAnimation() {
    return (
      (!this.spreads || !this.isPriorityImagesLoaded() || !this.isReady) &&
      this.errorCode === ''
    );
  }

  get showLoggedInTopBar() {
    return (
      this.isLoggedIn() &&
      !this.isAnnotatorEnabled &&
      (this.isReady || this.errorCode !== '')
    );
  }

  get isPageNumbersVisible() {
    return (
      this.showPageNumbers &&
      (this.isMobile || (!this.submitting && !this.isMobile))
    );
  }

  checkIfStoryOwner() {
    this.projectService
      .load({ id: this.story.project })
      .subscribe((projectData: Project) => {
        this.isStoryOwner = this.userAccount.data.id === projectData.owner_id;
        this.isReady = true;

        this.loggingService.log('isStoryOwner:', this.isStoryOwner, {
          appSection: LogAppSection.StoryShowing,
        });
        this.loggingService.log('isReady:', this.isReady, {
          appSection: LogAppSection.StoryShowing,
        });
      });
  }

  onChangePage(page: number) {
    if (page === 1 || page % 2 === 0) {
      this.currentPage = page;
      this.trackPageView();
    } else {
      this.currentPage = page - 1;
    }
    this.handlePageNumbersChange();
    this.cdRef.detectChanges();
  }

  trackPageView() {
    const percentage = this.currentPage / this.countPages;
    let percentageInterval;
    switch (true) {
      case percentage === 1:
        percentageInterval = '100';
        break;
      case percentage >= 0.75:
        percentageInterval = '75';
        break;
      case percentage >= 0.5:
        percentageInterval = '50';
        break;
      case percentage >= 0.25:
        percentageInterval = '25';
        break;
    }

    if (percentageInterval && !this.trackedPercentages[percentageInterval]) {
      this.trackedPercentages[percentageInterval] = true;
      this.firebaseService.logEvent(
        `${this.route.snapshot.queryParamMap.get('share_token') ? 'shared_' : ''}story_${percentageInterval}p_browse`,
        {
          story_id: this.story.id,
          project_id: this.projectId
        }
      );
    }
  }

  handlePageNumbersChange() {
    const spreadNumber = this.mapPageNumberToSpreadIndex(this.currentPage);
    if (spreadNumber === 3 && !this.userAccount.data.engaged_at) {
      this.currentUser.engageUser().subscribe(user => {
        this.userAccount.data.engaged_at = user.engaged_at;
        this.firebaseService.logEvent('user_activation');
        this.facebookPixelService.trackEvent('UserActivation', {});
      });
    }
    if (this.pageNumberTimerId) {
      clearTimeout(this.pageNumberTimerId);
    }
    this.pageNumberTimerId = setTimeout(() => {
      this.showPageNumbers = false;
      this.pageNumberTimerId = undefined;
    }, 1000);
  }

  onLoadImage(id) {
    if (this.loadedImages.find(item => item.id === id)) {
      return;
    }

    const priority = this.spreads.find(item => item.id === id).priority;
    if (priority) {
      this.loadedImages.push({ id, priority });
      this.cdRef.detectChanges();

      this.loggingService.log(
        `Loaded spread: ${id} ${priority} ${this.loadedImages.length}`,
        {
          appSection: LogAppSection.StoryShowing,
        }
      );

      if (this.isAllImagesLoaded()) {
        this.loggingService.log('All spreads loaded!', {
          appSection: LogAppSection.StoryShowing,
        });
      }
    }
  }

  onLoadPlaceholderImage(id) {
    if (this.loadedPlaceholderImages.find(item => item.id === id)) {
      return;
    }

    const priority = this.spreads.find(item => item.id === id).priority;

    if (!this.isPriorityImagesLoaded() && !priority) {
      this.loadedPlaceholderImages.push({ id, priority });
      this.cdRef.detectChanges();

      this.loggingService.log(
        `Loaded placeholder spread: ${id} ${priority} ${this.loadedPlaceholderImages.length}`,
        {
          appSection: LogAppSection.StoryShowing,
        }
      );
    }
  }

  isPriorityImagesLoaded() {
    return (
      this.loadedImages.filter(item => item.priority).length ===
      this.prioritySpreadsCount * 2 &&
      this.loadedPlaceholderImages.length ===
      this.spreads.length - this.prioritySpreadsCount * 2
    );
  }

  isAllImagesLoaded() {
    return this.loadedImages.length === this.spreads.length;
  }

  setCountPages(pages) {
    this.countPages = pages;
    this.cdRef.detectChanges();
  }

  setTurnInitialized(isInitialized) {
    this.isTurnInitialized = isInitialized;
    this.cdRef.detectChanges();

    this.loggingService.log('Turn initialized', {
      appSection: LogAppSection.StoryShowing,
    });

    if (!this.isAllImagesLoaded()) {
      this.setNonPrioritySpreadsUrls();
    }
  }

  setNonPrioritySpreadsUrls() {
    this.spreads.forEach((item, index, array) => {
      if (!item.priority) {
        array[index].urls = this.initialSpreads[index].urls;
      }
    });

    this.loggingService.log('Non-priority spread links set', {
      appSection: LogAppSection.StoryShowing,
    });
  }

  onChangeSpread(index) {
    this.currentPage = this.mapSpreadIndexToPageNumber(index);
    this.handlePageNumbersChange();
    this.cdRef.detectChanges();
  }

  getSpreadSize() {
    const picture = this.spreads.find(pic => pic.spread_type === 'spread');
    return {
      width: picture.width,
      height: picture.height,
    };
  }

  mapPageNumberToSpreadIndex(pageNumber) {
    if (pageNumber % 2 === 0) {
      return pageNumber / 2;
    } else {
      return (pageNumber - 1) / 2;
    }
  }

  mapSpreadIndexToPageNumber(currentSlide) {
    const pageNumber = currentSlide * 2;
    return pageNumber === 0 ? 1 : pageNumber;
  }

  closeShareModal() {
    this.isShareModalOpened = false;
  }

  getBookCover() {
    this.storyCover = this.story.cover_url;
  }

  actionsAvailable() {
    return this.isLoggedIn() || environment.guestViewAlbumActionsVisible;
  }

  openShareModal() {
    this.getBookCover();
    if (!this.isLoggedIn()) {
      this.accountService.openModal(() => {
        this.openShareModal();
      });
      return;
    }

    this.isShareModalOpened = true;
    if (!this.shareLink) {
      this.isShareLinkGenerationInProgress = true;
      this.isShareLinkGenerationHasFailed = false;

      this.shareService
        .getInvitationLink(this.projectId)
        .subscribe(
          (data: ShareLink) => {
            this.shareLink = data;
            this.isShareLinkGenerationInProgress = false;
            this.firebaseService.logEvent('story_share', {
              story_id: this.story.id,
              project_id: this.projectId,
            });
            this.facebookPixelService.trackEvent('StoryShare', {
              story_id: this.story.id,
            });
          },
          () => {
            this.isShareLinkGenerationHasFailed = true;
            this.isShareLinkGenerationInProgress = false;
          }
        );
    }
  }
  goToInvitation() {
    if (this.isLoggedIn()) {
      window.setTimeout(() => {
        this.openInvitationModal();
      }, 500);
    }
  }

  openInvitationModal() {
    this.getBookCover();
    // if (!this.isLoggedIn()) {
    //   this.accountService.openModal(() => {
    //     this.openShareModal();
    //   });
    //   return;
    // }

    this.isShareModalOpened = true;
    if (!this.shareLink) {
      this.isShareLinkGenerationInProgress = true;
      this.isShareLinkGenerationHasFailed = false;
      this.contribService
        .getInvitationLink(this.projectId)
        .subscribe(response => {
          this.shareLink = response;
          this.isShareLinkGenerationInProgress = false;
        });
    }
  }

  onCopy() {
    this.isSuccessfullyCopiedMessageShown = true;
    setTimeout(() => {
      this.isSuccessfullyCopiedMessageShown = false;
    }, 2000);
  }

  enableAnnotator() {
    this.isAnnotatorEnabled = true;
  }

  disableAnnotator() {
    this.isAnnotatorEnabled = false;
  }

  goToEdit() {
    this.firebaseService.logEvent('edit_mode_enter', {});
    this.facebookPixelService.trackEvent('EditEnter', {});
    if (this.isLoggedIn()) {
      window.setTimeout(() => {
        window.location.href = this.storyService.getEditLink(this.story.id);
      }, 500);
    } else {
      this.accountService.openModal(() => {
        this.goToEdit();
      });
    }
  }

  orderPrint() {
    if (this.isLoggedIn()) {
      this.router.navigate(['/stories', this.story.id, 'print']);
      this.firebaseService.logEvent('checkout_enter', {
        story_id: this.story.id,
        project_id: this.projectId,
        event_source: 'story_preview_toolbar',
        spread_sequence: this.currentPage / 2,
      });
      this.facebookPixelService.trackEvent('InitiateCheckout', {
        story_id: this.story.id,
      });
    } else {
      this.accountService.openModal(() => {
        this.orderPrint();
      });
    }
  }

  isLoggedIn() {
    return this.currentUser.get();
  }
}
